import { RadialHalftoneImagePhase } from 'app/views/home/webgl/particlesOptimised/departments/screens/phases/RadialHalftoneImagePhase';
import { DropMachinePhase } from 'app/views/home/webgl/particlesOptimised/departments/screens/phases/DropMachinePhase';
import { SwirlPhase } from 'app/views/home/webgl/particlesOptimised/departments/screens/phases/SwirlPhase';

import type { PhaseEntry } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';
import type { ScreensContext } from 'app/views/home/webgl/particlesOptimised/departments/screens/ScreensContext';

/**
 * The screens show, in order, with timings — THE place to reorder, re-time or
 * add screens phases (uniform with spaces/spacesPhaseEntries.ts and
 * systems/systemsPhaseEntries.ts). One class per phase lives in ./phases/;
 * order and timings are verbatim from the reference stage table
 * (particles/departments/ScreensDepartment: _sectionsOrder =
 * ['radialHalftoneImage', 'dropMachine', 'swirl'] and _nextScreensSection's
 * _roughFrameSecs(9 / 8.8 / 7) writes). ScreensDepartment.ts is only the
 * lifecycle shell around this list.
 *
 * The screens durations are DYNAMIC — the first live use of the framework's
 * frames-as-function seam (phases.types.ts): the reference computes
 * _switchShapeAtFrameCount per arm inside _nextScreensSection —
 * `Math.round(secs * 60)` (_roughFrameSecs), then `*= 0.55` when
 * _reduceFirstPass is still set (the shortened first pass after every scene
 * start). The flag lives on the ScreensContext; it is read here at
 * frame-resolution time (PhaseRunner resolves frames at phase start, before
 * start() runs) and again by the halftone phase's combo timings, then
 * cleared by the department's onStarted hook — the reference's
 * read-then-clear order.
 */
export function createScreensPhaseEntries(ctx: ScreensContext): Array<PhaseEntry> {
	//_roughFrameSecs + the conditional first-pass cut, per entry:
	const roughFrameSecs = (secs: number) => () => Math.round(secs * 60) * (ctx.reduceFirstPass ? 0.55 : 1);

	return [
		{ phase: new RadialHalftoneImagePhase(ctx), frames: roughFrameSecs(9) },
		{ phase: new DropMachinePhase(ctx), frames: roughFrameSecs(8.8) },
		{ phase: new SwirlPhase(ctx), frames: roughFrameSecs(7) }
	];
}
